import { Directive, ElementRef, Input, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Tooltip } from 'bootstrap';

@Directive({
  selector: '[certTooltip]',
  standalone: false,
})
export class TooltipDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('certTooltip') tooltipText: string | null | undefined = '';
  @Input() enableTooltip = true;
  @Input() tooltipPlacement: Tooltip.PopoverPlacement = 'top';
  @Input() tooltipTrigger: Tooltip.Options['trigger'] = 'hover';
  @Input() tooltipDelay = 0;

  private tooltip: Tooltip | null = null;
  private viewInitialized = false;

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.initializeTooltip();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.viewInitialized && Object.values(changes).some((change) => !change.firstChange)) {
      this.dispose();
      this.initializeTooltip();
    }
  }

  private initializeTooltip() {
    if (!this.enableTooltip || !this.tooltipText) return;

    // Dispose any existing tooltip first
    this.dispose();

    try {
      this.tooltip = new Tooltip(this.el.nativeElement, {
        title: this.tooltipText,
        html: true,
        placement: this.tooltipPlacement,
        trigger: this.tooltipTrigger,
        delay: this.tooltipDelay,
        sanitize: false,
        container: 'body',
      });

      const internal: any = this.tooltip;
      internal._activeTrigger = internal._activeTrigger || {};
    } catch {
      // Silently fail if tooltip initialization fails
      this.tooltip = null;
    }
  }

  private dispose() {
    if (!this.tooltip) return;

    try {
      const internal: any = this.tooltip;

      // Ensure _element exists
      if (!internal._element || internal._element === null) {
        internal._element = this.el.nativeElement ?? document.createElement('span');
      }

      // Ensure _activeTrigger exists
      if (!internal._activeTrigger || typeof internal._activeTrigger !== 'object') {
        internal._activeTrigger = {};
      }

      // Skip hide() animation - go straight to dispose
      // This prevents Bootstrap's transition callbacks from firing on destroyed elements
      const tip = internal._getTipElement();
      if (tip) {
        tip.remove();
      }

      this.tooltip.dispose();
    } catch {
      // Silently swallow any Bootstrap cleanup errors
    } finally {
      this.tooltip = null;
    }
  }

  ngOnDestroy() {
    this.dispose();
  }
}
